import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchAuthSession, signIn, signOut, getCurrentUser } from 'aws-amplify/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (idToken) {
        const groups = idToken.payload['cognito:groups'] || [];
        const tenantId = idToken.payload['custom:tenant_id'] || '';
        const displayName = idToken.payload['custom:display_name'] || currentUser.username;

        setUser({
          userId: currentUser.userId,
          username: currentUser.username,
          email: idToken.payload.email,
          groups: Array.isArray(groups) ? groups : [groups],
          tenantId,
          displayName,
          token: session.tokens?.accessToken?.toString(),
          idToken: idToken.toString(),
        });
      }
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const result = await signIn({ username: email, password });
    if (result.isSignedIn) {
      await loadUser();
    }
    return result;
  };

  const logout = async () => {
    await signOut();
    setUser(null);
  };

  const hasGroup = (group) => {
    return user?.groups?.includes(group) || false;
  };

  const hasAnyGroup = (groups) => {
    return groups.some((g) => user?.groups?.includes(g));
  };

  const isInternal = () => {
    return hasAnyGroup(['underwriter', 'leadership']);
  };

  const getToken = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.idToken?.toString();
    } catch {
      return null;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasGroup,
    hasAnyGroup,
    isInternal,
    getToken,
    refreshUser: loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
